import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Input, Checkbox, notification, Button } from 'antd';
import { FloppyDisk, Trash } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useOneButtonCapability } from '../../../../hooks/useOneButtonCapability';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/ContactManageModal.css';
import './oneButton-css/OneButtonFormControls.css';

/** Portal target in DashboardModal footer (split contacts layout). */
export const OBD_CONTACTS_PANEL_FOOTER_ACTIONS_ID = 'obd-contacts-panel-footer-actions';

export interface ContactFormState {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    phoneType: string;
    contactType: string;
    preferredContactMethod: string;
    preferredLanguage: string;
    isPrimaryContact: boolean;
    isSecondaryContact: boolean;
    onUtilityBill: boolean;
    addToContract: boolean;
    homeowner: boolean;
    onTitle: boolean;
    sameAsInstallationAddress: boolean;
    mailingStreet: string;
    mailingCity: string;
    mailingState: string;
    mailingZip: string;
    companyTag?: string;
    contactLabel?: string;
}

export interface ValidationErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    mailingStreet?: string;
    mailingCity?: string;
    mailingState?: string;
    mailingZip?: string;
    isPrimaryContact?: string;
}


const CONTACT_TYPE_OPTIONS = [
    { id: 'primary', label: 'Primary Contact', key: 'isPrimaryContact' as const },
    { id: 'secondary', label: 'Secondary Contact', key: 'isSecondaryContact' as const },
    { id: 'utility', label: 'On Utility Bill', key: 'onUtilityBill' as const },
    { id: 'contract', label: 'Add to Contract', key: 'addToContract' as const },
    { id: 'homeowner', label: 'Homeowner', key: 'homeowner' as const },
    { id: 'onTitle', label: 'On Title', key: 'onTitle' as const },
];

interface SelectOption {
    value: string;
    label: string;
}

const defaultFormState: ContactFormState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneType: '2',
    contactType: '77',
    preferredContactMethod: '6',
    preferredLanguage: '4',
    isPrimaryContact: false,
    isSecondaryContact: false,
    onUtilityBill: false,
    addToContract: false,
    homeowner: false,
    onTitle: false,
    sameAsInstallationAddress: true,
    mailingStreet: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    companyTag: '',
    contactLabel: '',
};

function customerToFormState(c: any): ContactFormState {
    return {
        firstName: c?.FirstName ?? c?.firstName ?? '',
        lastName: c?.LastName ?? c?.lastName ?? '',
        email: c?.Email ?? c?.email ?? '',
        phone: c?.Phone ?? c?.phone ?? '',
        phoneType: (c?.PhoneType !== undefined && c?.PhoneType !== null) ? String(c.PhoneType) : (c?.phoneType !== undefined && c?.phoneType !== null ? String(c.phoneType) : ''),
        contactType: (c?.ContactType !== undefined && c?.ContactType !== null) ? String(c.ContactType) : (c?.contactType !== undefined && c?.contactType !== null ? String(c.contactType) : ''),
        preferredContactMethod: (c?.PreferredContactMethod !== undefined && c?.PreferredContactMethod !== null) ? String(c.PreferredContactMethod) : (c?.preferredContactMethod !== undefined && c?.preferredContactMethod !== null ? String(c.preferredContactMethod) : ''),
        preferredLanguage: (c?.PreferredLanguage !== undefined && c?.PreferredLanguage !== null) ? String(c.PreferredLanguage) : (c?.preferredLanguage !== undefined && c?.preferredLanguage !== null ? String(c.preferredLanguage) : ''),
        isPrimaryContact: !!(c?.IsPrimaryContact ?? c?.isPrimaryContact),
        isSecondaryContact: !!(c?.IsSecondaryContact ?? c?.isSecondaryContact),
        onUtilityBill: !!(c?.IsUtilityBillOwner ?? c?.isUtilityBillOwner),
        addToContract: !!(c?.AddToContract ?? c?.addToContract),
        homeowner: !!(c?.OnTitleCheck ?? c?.onTitleCheck) ? false : !!(c?.IsHomeowner ?? c?.IsHomeOwner ?? c?.isHomeowner ?? c?.isHomeOwner),
        onTitle: !!(c?.OnTitleCheck ?? c?.onTitleCheck),
        sameAsInstallationAddress: c?.SameAsInstallationAddress ?? c?.sameAsInstallationAddress ?? true,
        mailingStreet: c?.InsStreet ?? c?.insStreet ?? c?.MailingStreet ?? c?.mailingStreet ?? c?.OtherStreet ?? c?.otherStreet ?? c?.Street ?? c?.street ?? '',
        mailingCity: c?.InsCity ?? c?.insCity ?? c?.MailingCity ?? c?.mailingCity ?? c?.OtherCity ?? c?.otherCity ?? c?.City ?? c?.city ?? '',
        mailingState: (c?.InsState ?? c?.insState ?? c?.MailingState ?? c?.mailingState ?? c?.OtherState ?? c?.otherState ?? c?.State ?? c?.state ?? '')?.toUpperCase(),
        mailingZip: c?.InsZipCode ?? c?.insZipCode ?? c?.MailingPostalCode ?? c?.mailingPostalCode ?? c?.MailingZipCode ?? c?.mailingZipCode ?? c?.OtherZipCode ?? c?.otherZipCode ?? c?.ZipCode ?? c?.zipCode ?? '',
        companyTag: c?.Company ?? c?.CompanyName ?? c?.company ?? c?.companyName ?? '',
        contactLabel: c?.ContactLabel ?? c?.contactLabel ?? '',
    };
}

/** 
 * Formats raw digits into (XXX) XXX-XXXX
 */
const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export interface ContactManageModalProps {
    open: boolean;
    onClose: () => void;
    contact: any | null;
    contactIndex: number;
    /** When provided, form initializes from this (e.g. saved overrides) merged with contact */
    initialOverride?: Partial<ContactFormState>;
    onSave?: (index: number, data: ContactFormState, emailWarning?: string) => void;
    onDelete?: (index: number) => void;
    onDeleteNew?: (index: number) => void;
    /** Panel mode: live form updates (e.g. sync detail hero tags while editing). */
    onFormChange?: (data: ContactFormState) => void;
    /** Unsaved per-contact overrides (split-panel) so primary counts stay accurate before save. */
    contactOverrides?: Record<number, Partial<ContactFormState>>;
    /** `panel` renders form inline (contacts modal split); default opens nested dashboard modal */
    presentation?: 'modal' | 'panel';
}

const PRIMARY_CONTACT_LIMIT_MESSAGE =
    'Only one contact can be designated as the Primary Contact. Please unselect the existing Primary Contact and try again.';

export const ContactManageModal: React.FC<ContactManageModalProps> = ({
    open,
    onClose,
    contact,
    contactIndex,
    initialOverride,
    onSave,
    onDelete,
    onDeleteNew,
    onFormChange,
    contactOverrides,
    presentation = 'modal',
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityinfoMasterData = useOneButtonStore((s) => s.opportunityinfoMasterData);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const financingGrid = useOneButtonStore((s) => s.financingGrid);
    const { getCapabilityValue, hasCapability } = useOneButtonCapability();

    const [form, setForm] = useState<ContactFormState>(defaultFormState);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [submitted, setSubmitted] = useState(false);
    const [mailingAddressBackup, setMailingAddressBackup] = useState<Partial<ContactFormState> | null>(null);
    
    const [noticeAPI, noticeContext] = notification.useNotification({ maxCount: 4, stack: false });
    const [warningDialog, setWarningDialog] = useState<{ title: string; content: string } | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [panelFooterPortal, setPanelFooterPortal] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (presentation !== 'panel' || !open) {
            setPanelFooterPortal(null);
            return;
        }
        const syncPortal = () => {
            setPanelFooterPortal(document.getElementById(OBD_CONTACTS_PANEL_FOOTER_ACTIONS_ID));
        };
        syncPortal();
        const t = window.setTimeout(syncPortal, 0);
        return () => window.clearTimeout(t);
    }, [presentation, open]);

    const isLightreach = useMemo(() => {
        if (financingGrid?.some(r => (r.partner || '').toLowerCase().includes('lightreach'))) return true;
        const products = opportunityDetails?.OpportunityProduct || opportunityDetails?.opportunityProduct || [];
        return products.some((p: any) => {
            const pn = (p.Finance_Partner_Display_Name || p.Finance_Partner || '').toLowerCase();
            return pn.includes('lightreach');
        });
    }, [financingGrid, opportunityDetails]);

    const creditPassed = useMemo(() => {
        if (!contact) return false;
        if (contact.CreditCheckStatus === true || contact.creditCheckStatus === true) return true;
        const passStr = String(contact.CreditPassStatus || contact.creditPassStatus || '').toLowerCase();
        if (passStr === 'passed') return true;
        if (contact.financePartnerCreditStatusList?.IsAnyCreditStatusPass) return true;
        return false;
    }, [contact]);

    const anyCreditPassed = useMemo(() => {
        if (opportunityDetails?.IsAnyCreditPassed) return true;
        const customers = opportunityDetails?.Customers || [];
        return customers.some((c: any) => {
            // Check boolean status from provided JSON
            if (c.CreditCheckStatus === true || c.creditCheckStatus === true) return true;
            // Check string status
            const status = String(c.CreditPassStatus || c.creditPassStatus || '').toLowerCase();
            if (status === 'passed') return true;
            // Check nested financial pass status from JSON
            if (c.financePartnerCreditStatusList?.IsAnyCreditStatusPass) return true;
            return false;
        });
    }, [opportunityDetails]);

    const isFieldLocked = useMemo(() => {
        // Only lock if THIS specific contact passed credit AND it's Lightreach
        // But always allow editing for NEW contacts (no CustomerId)
        const contactId = contact?.CustomerId || contact?.customerId;
        if (!contactId) return false;
        
        return creditPassed && isLightreach;
    }, [creditPassed, isLightreach, contact]);

    const opportunityInfoAccess = useMemo(() => getCapabilityValue('Opportunity_Information_Access'), [getCapabilityValue]);
    const homeOwnerAccess = useMemo(() => getCapabilityValue('Home_Owner'), [getCapabilityValue]);

    const isOpportunityInfoReadOnly = opportunityInfoAccess === 'View';
    const showSecondaryContact = hasCapability('Secondary_Contact');
    const showHomeOwner = hasCapability('Home_Owner');
    const isHomeOwnerReadOnly = homeOwnerAccess === 'View';

    const isCapabilityReadOnly = isOpportunityInfoReadOnly;
    const isCustomerInfoAndAddressLocked = isFieldLocked || isCapabilityReadOnly;
    const isContactTypeLocked = isCapabilityReadOnly;

    const mapToOptions = (items: any[]) => 
        items?.map((item: any) => ({ 
            value: item.Value !== undefined ? String(item.Value) : (item.Id !== undefined ? String(item.Id) : String(item.Text || item)), 
            label: item.Text || String(item) 
        })) || [];

    const phoneTypes = useMemo(() => {
        const data = opportunityinfoMasterData?.PhoneType || opportunityinfoMasterData?.phoneType;
        return mapToOptions(data);
    }, [opportunityinfoMasterData]);
    const titleCase = (str: string) => 
        str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

    const getTotalAddToContractCount = useCallback(() => {
        let count = 0;
        const customers = opportunityDetails?.Customers || [];
        customers.forEach((c: any, i: number) => {
            if (i !== contactIndex) {
                // Robust property check for Add To Contract OR Primary Contact (which implies Add to Contract)
                const isPrimary = c.IsPrimaryContact || c.isPrimaryContact;
                const isSecondary = c.IsSecondaryContact || c.isSecondaryContact;
                const isAddToContract = c.AddToContract || c.addToContract || isPrimary || isSecondary;
                if (isAddToContract) count++;
            }
        });
        return count;
    }, [opportunityDetails, contactIndex]);

    const getTotalHomeownerCount = useCallback(() => {
        let count = 0;
        const customers = opportunityDetails?.Customers || [];
        customers.forEach((c: any, i: number) => {
            if (i !== contactIndex) {
                if (c.IsHomeowner || c.IsHomeOwner || c.homeowner || c.isHomeowner) count++;
            }
        });
        return count;
    }, [opportunityDetails, contactIndex]);

    const isCustomerPrimary = useCallback((c: any, index: number) => {
        const override = contactOverrides?.[index];
        if (override?.isPrimaryContact !== undefined) return override.isPrimaryContact;
        return !!(c?.IsPrimaryContact || c?.isPrimaryContact);
    }, [contactOverrides]);

    const getTotalPrimaryCount = useCallback(() => {
        let count = 0;
        const customers = opportunityDetails?.Customers || [];
        customers.forEach((c: any, i: number) => {
            if (i !== contactIndex && isCustomerPrimary(c, i)) count++;
        });
        return count;
    }, [opportunityDetails, contactIndex, isCustomerPrimary]);

    const otherPrimaryExists = getTotalPrimaryCount() > 0;

    const states = useMemo(() => {
        const items = opportunityinfoMasterData?.StatesFullName || [];
        return items.map((item: any) => {
            const rawLabel = item.Text || String(item);
            const rawValue = item.Value !== undefined ? String(item.Value) : (item.Id !== undefined ? String(item.Id) : String(rawLabel));
            const formattedLabel = rawLabel.split(' ').map(titleCase).join(' ');
            return { value: rawValue, label: formattedLabel };
        });
    }, [opportunityinfoMasterData]);
    const languages = useMemo(() => mapToOptions(opportunityinfoMasterData?.PreferredLanguage), [opportunityinfoMasterData]);
    const contactTypes = useMemo(() => mapToOptions(opportunityinfoMasterData?.contactType), [opportunityinfoMasterData]);
    const contactMethods = useMemo(() => mapToOptions(opportunityinfoMasterData?.PreferredContactMethod), [opportunityinfoMasterData]);

    useEffect(() => {
        if (open) {
            setErrors({});
            setSubmitted(false);
            setMailingAddressBackup(null); // Reset backup when opening/switching contact

            if (contact) {
                const base = customerToFormState(contact);
                const merged = initialOverride ? { ...base, ...initialOverride } : base;
                
                // If same as installation is true, sync from opportunity details if fields are empty
                if (merged.sameAsInstallationAddress && opportunityDetails?.InstallationAddress) {
                    const addr = opportunityDetails.InstallationAddress;
                    if (!merged.mailingStreet) merged.mailingStreet = addr.Street || addr.street || '';
                    if (!merged.mailingCity) merged.mailingCity = addr.City || addr.city || '';
                    if (!merged.mailingState) merged.mailingState = addr.State || addr.state || addr.stateCode || '';
                    if (!merged.mailingZip) merged.mailingZip = addr.ZipCode || addr.zipCode || '';
                }

                // Angular setCustomerInformations: primary implies on contract when slot allows.
                if (merged.isPrimaryContact && !merged.addToContract) merged.addToContract = true;

                merged.phone = formatPhoneNumber(merged.phone);
                setForm(merged);
                validate(merged);
            } else {
                const primary = opportunityDetails?.Customers?.find((c: any) => c.IsPrimaryContact);
                const installAddr = opportunityDetails?.InstallationAddress;
                const base = { ...defaultFormState, ...initialOverride };
                
                if (primary) {
                    if (!base.email) base.email = primary.Email || '';
                    if (!base.phone) base.phone = primary.Phone || '';
                }

                if (installAddr && base.sameAsInstallationAddress) {
                    base.mailingStreet = installAddr.Street || '';
                    base.mailingCity = installAddr.City || '';
                    base.mailingState = installAddr.State || installAddr.stateCode || '';
                    base.mailingZip = installAddr.ZipCode || installAddr.zipCode || '';
                }

                setForm(base);
                validate(base);
            }
        }
    }, [open, contactIndex]); // Only re-run when modal opens or contact index changes

    useEffect(() => {
        if (!open || presentation !== 'panel' || !onFormChange) return;
        onFormChange(form);
    }, [form, open, presentation, onFormChange]);

    const displayName = `${form.firstName} ${form.lastName}`.trim() || 'New contact';
    const update = (key: keyof ContactFormState, value: any) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            validate(next);
            return next;
        });
    };

    const validate = (state: ContactFormState = form): boolean => {
        const newErrors: ValidationErrors = {};
        
        if (!state.firstName?.trim()) newErrors.firstName = 'First Name is required';
        if (!state.lastName?.trim()) newErrors.lastName = 'Last Name is required';
        
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
        if (!state.email?.trim()) {
            newErrors.email = 'Valid Email is required';
        } else if (!emailRegex.test(state.email)) {
            newErrors.email = 'Valid Email is required';
        }

        if (!state.phone?.trim()) {
            newErrors.phone = 'Valid Phone Number is required';
        } else {
            const digits = state.phone.replace(/\D/g, '');
            if (digits.length < 10) {
                newErrors.phone = 'Valid Phone Number is required';
            }
        }

        if (!state.sameAsInstallationAddress) {
            if (!state.mailingStreet?.trim()) newErrors.mailingStreet = 'Street is required';
            if (!state.mailingCity?.trim()) newErrors.mailingCity = 'City is required';
            if (!state.mailingState?.trim()) newErrors.mailingState = 'State is required';
            
            const zipRegex = /^[0-9]{5}$/;
            if (!state.mailingZip?.trim()) {
                newErrors.mailingZip = 'Zip Code is required';
            } else if (!zipRegex.test(state.mailingZip)) {
                newErrors.mailingZip = 'Zip Code is not valid.';
            }
        }

        if (state.isPrimaryContact && getTotalPrimaryCount() > 0) {
            newErrors.isPrimaryContact = PRIMARY_CONTACT_LIMIT_MESSAGE;
        }
        let customers = opportunityDetails?.Customers || [];
        const updatedCustomerList = customers.map((cust:any, i:number) => {
            const isTarget = i === contactIndex;
            const custId = cust?.Id || cust?.CustomerId || 0;
            
            const baseFields = {
                FirstName: isTarget ? state.firstName : (cust?.FirstName || ''),
                LastName: isTarget ? state.lastName : (cust?.LastName || ''),
                Email: isTarget ? state.email : (cust?.Email || ''),
                Phone: isTarget ? state.phone : (cust?.Phone || ''),
                PhoneType: isTarget ? parseInt(state.phoneType, 10) : (cust?.PhoneType || 2),
                ContactType: isTarget ? parseInt(state.contactType, 10) : (cust?.ContactType || 77),
                PreferredContactMethod: isTarget ? parseInt(state.preferredContactMethod, 10) : (cust?.PreferredContactMethod || 6),
                PreferredLanguage: isTarget ? parseInt(state.preferredLanguage, 10) : (cust?.PreferredLanguage || 4),
                SameAsInstallationAddress: isTarget ? state.sameAsInstallationAddress : (cust?.SameAsInstallationAddress ?? true),
                InsStreet: isTarget ? state.mailingStreet : (cust?.InsStreet || cust?.Street || ''),
                InsCity: isTarget ? state.mailingCity : (cust?.InsCity || cust?.City || ''),
                InsState: isTarget ? state.mailingState : (cust?.InsState || cust?.State || ''),
                InsZipCode: isTarget ? state.mailingZip : (cust?.InsZipCode || cust?.ZipCode || ''),
                IsPrimaryContact: isTarget ? state.isPrimaryContact : (state.isPrimaryContact ? false : (cust?.IsPrimaryContact || false)),
                IsSecondaryContact: isTarget ? state.isSecondaryContact : (state.isSecondaryContact ? false : (cust?.IsSecondaryContact || false)),
                IsUtilityBillOwner: isTarget ? state.onUtilityBill : (cust?.IsUtilityBillOwner ?? false),
                IsHomeOwner: isTarget ? state.homeowner : (cust?.IsHomeowner ?? false),
                AddToContract: isTarget ? state.addToContract : (cust?.AddToContract ?? false),
                OnTitleCheck: isTarget ? state.onTitle : (cust?.OnTitleCheck ?? false),
            };

            return {
                ...cust,
                ...baseFields,
                CustomerId: custId,
                index: i + 1,
            };
        });
        opportunityDetails.Customers = updatedCustomerList;
        customers[contactIndex] = {
            ...customers[contactIndex],
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (isCapabilityReadOnly) return;
        setSubmitted(true);
        
        // 1. Basic Field Validation (inline errors: Name, Phone, Address)
        const isBasicValid = validate();
        
        // 2. Business Logic Validation (modalAPI)
        let emailWarning = '';
        if (form.email) {
            const customers = opportunityDetails?.Customers || [];
            const isDuplicatePair = customers.some((c: any, i: number) => {
                if (i === contactIndex) return false;
                const otherEmail = (c.Email || c.email || '').toLowerCase();
                if (otherEmail !== form.email.toLowerCase()) return false;
                const otherIsPrimary = !!(c.IsPrimaryContact || c.isPrimaryContact);
                const otherIsSecondary = !!(c.IsSecondaryContact || c.isSecondaryContact);
                if (form.isPrimaryContact && otherIsSecondary) return true;
                if (form.isSecondaryContact && otherIsPrimary) return true;
                return false;
            });
            if (isDuplicatePair) emailWarning = 'The Primary Contact and Secondary Contact have the same email address. Please validate with the customer and if possible ensure each contact has a distinct email.';
        }
        
        const totalPrimary = getTotalPrimaryCount() + (form.isPrimaryContact ? 1 : 0);
        let primaryWarning = '';
        if (totalPrimary === 0) {
            primaryWarning = 'No contact has been added as a Primary Contact';
        } else if (totalPrimary > 1) {
            primaryWarning = PRIMARY_CONTACT_LIMIT_MESSAGE;
        }

        // 3. Decision Logic
        if (isBasicValid && !primaryWarning) {
            onSave?.(contactIndex, form, emailWarning);
            if (presentation !== 'panel') {
                onClose();
            }
            return;
        }

        // If basic fields are valid but business logic fails, show the modal warning
        if (isBasicValid) {
            if (primaryWarning) {
                setWarningDialog({
                    title: totalPrimary > 1 ? 'Primary Contact Limit' : 'Primary Contact Required',
                    content: primaryWarning,
                });
            }
        }
        // If basic fields are invalid, validate() has already called setErrors({}) 
        // and the UI will show red text under the inputs.
    };

    const canDelete = useMemo(() => {
        if (!contact) return false;
        if (isCapabilityReadOnly) return false;
        
        // Use property names from both API response and state
        const contactId = contact.CustomerId || contact.customerId;
        const isPrimary = !!(contact.IsPrimaryContact || contact.isPrimaryContact);
        const hasPassedCredit = !!(contact.CreditCheckStatus || contact.creditCheckStatus);

        // Rule: Newly added records (no CustomerId) can always be removed
        if (!contactId) return true;

        // Rule: Cannot delete if Primary, passed credit, or key legal roles are assigned
        if (isPrimary) return false;
        if (hasPassedCredit) return false;
        if (form.onTitle) return false;
        if (form.homeowner) return false;
        
        return true;
    }, [contact, form.onTitle, form.homeowner]);

    const handleDelete = () => {
        setDeleteConfirmOpen(true);
    };

    const resetIndex = () =>{
        onDeleteNew?.(contactIndex);
    }

    const actionButtons = (
        <>
            {canDelete && (
                <button
                    type="button"
                    className="obd-contact-modal__btn obd-contact-modal__btn--delete"
                    onClick={handleDelete}
                >
                    <Trash size={16} weight="bold" aria-hidden />
                    Delete
                </button>
            )}
            {!contact && !!contactIndex && (
                <button
                    type="button"
                    className="obd-contact-modal__btn obd-contact-modal__btn--delete"
                    onClick={resetIndex}
                >
                    <Trash size={16} weight="bold" aria-hidden />
                    Delete
                </button>
            )}
            <button
                type="button"
                className="obd-contact-modal__btn obd-contact-modal__btn--primary"
                onClick={handleSave}
                disabled={isCapabilityReadOnly}
            >
                <FloppyDisk size={14} weight="bold" aria-hidden />
                Save
            </button>
        </>
    );

    const footerActions = <div className="obd-contact-modal__actions">{actionButtons}</div>;

    const confirmDialogs = (
        <>
            {warningDialog && (
                <DashboardModal
                    open
                    onClose={() => setWarningDialog(null)}
                    title={warningDialog.title}
                    width={460}
                    theme={theme}
                    className={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    rootClassName={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    zIndex={20000}
                    maskClosable={false}
                    footer={
                        <Button type="primary" onClick={() => setWarningDialog(null)}>
                            OK
                        </Button>
                    }
                >
                    <div>{warningDialog.content}</div>
                </DashboardModal>
            )}
            {deleteConfirmOpen && (
                <DashboardModal
                    open
                    onClose={() => setDeleteConfirmOpen(false)}
                    title="Delete Contact"
                    width={460}
                    theme={theme}
                    className={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    rootClassName={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    zIndex={20000}
                    maskClosable={false}
                    footer={
                        <>
                            <Button onClick={() => setDeleteConfirmOpen(false)}>No</Button>
                            <Button
                                type="primary"
                                danger
                                onClick={() => {
                                    onDelete?.(contactIndex);
                                    setDeleteConfirmOpen(false);
                                    if (presentation === 'modal') onClose();
                                }}
                            >
                                Yes
                            </Button>
                        </>
                    }
                >
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Trash size={20} color="#ff4d4f" />
                        <span>Are you sure you want to delete {displayName}?</span>
                    </div>
                </DashboardModal>
            )}
        </>
    );

    const formFields = (
                <div className="obd-contact-modal__grid">
                    <section className="obd-contact-modal__section">
                        <h3 className="obd-contact-modal__section-title">Customer information</h3>
                        <div className="obd-contact-modal__fields">
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">First Name *</label>
                                <Input
                                    value={form.firstName}
                                    onChange={(e) => update('firstName', e.target.value)}
                                    placeholder="First name"
                                    className={`obd-contact-modal__input ${errors.firstName ? 'is-invalid' : ''}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                                {errors.firstName && <div className="invalid-feedbacks">{errors.firstName}</div>}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">Last Name *</label>
                                <Input
                                    value={form.lastName}
                                    onChange={(e) => update('lastName', e.target.value)}
                                    placeholder="Last name"
                                    className={`obd-contact-modal__input ${errors.lastName ? 'is-invalid' : ''}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                                {errors.lastName && <div className="invalid-feedbacks">{errors.lastName}</div>}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">Email *</label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => update('email', e.target.value)}
                                    placeholder="Email"
                                    className={`obd-contact-modal__input ${errors.email ? 'is-invalid' : ''}`}
                                    disabled={isCapabilityReadOnly}
                                />
                                {errors.email && <div className="invalid-feedbacks">{errors.email}</div>}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">Phone Number *</label>
                                <Input
                                    value={form.phone}
                                    onChange={(e) => update('phone', formatPhoneNumber(e.target.value))}
                                    placeholder="(555) 000-0000"
                                    className={`obd-contact-modal__input ${errors.phone ? 'is-invalid' : ''}`}
                                    disabled={isCapabilityReadOnly}
                                    maxLength={14}
                                />
                                {errors.phone && <div className="invalid-feedbacks">{errors.phone}</div>}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Phone Type</label>
                                <OBDSelect
                                    value={form.phoneType}
                                    onChange={(v) => update('phoneType', v)}
                                    options={phoneTypes}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={true}
                                />
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Contact Type</label>
                                <OBDSelect
                                    value={form.contactType}
                                    onChange={(v) => update('contactType', v)}
                                    options={contactTypes}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Preferred Contact Method</label>
                                <OBDSelect
                                    value={form.preferredContactMethod}
                                    onChange={(v) => update('preferredContactMethod', v)}
                                    options={contactMethods}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Preferred Language</label>
                                <OBDSelect
                                    value={form.preferredLanguage}
                                    onChange={(v) => update('preferredLanguage', v)}
                                    options={languages}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                            </div>
                        </div>

                    </section>

                    <div className="obd-contact-modal__column">
                        <section className="obd-contact-modal__section obd-contact-modal__section--types">
                            <h3 className="obd-contact-modal__section-title">Contact type</h3>
                            <div className="obd-contact-modal__type-grid">
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.isPrimaryContact ? 'obd-contact-modal__type-tile--selected' : ''} ${form.isSecondaryContact || (otherPrimaryExists && !form.isPrimaryContact) ? 'obd-contact-modal__type-tile--disabled' : ''}`}
                                        onClick={() => {
                                            if (isContactTypeLocked) return;
                                            if (form.isSecondaryContact) return;
                                        const newVal = !form.isPrimaryContact;
                                        if (newVal) {
                                            if (getTotalPrimaryCount() > 0) {
                                                setWarningDialog({
                                                    title: 'Primary Contact Limit',
                                                    content: PRIMARY_CONTACT_LIMIT_MESSAGE,
                                                });
                                                return;
                                            }
                                            const contractCount = getTotalAddToContractCount();
                                            if (contractCount >= 2) {
                                                setWarningDialog({
                                                    title: 'Contract Limit Reached',
                                                    content: 'You can not select more than two Add to Contract. Please unselect anyone and try again.',
                                                });
                                                return;
                                            }
                                        }

                                        setForm(prev => {
                                            // Angular ifContactPrimary(contactTypePrimary): turning primary ON sets
                                            // contactTypeAddToContract true; turning OFF clears add-to-contract.
                                            const next = {
                                                ...prev,
                                                isPrimaryContact: newVal,
                                                isSecondaryContact: false,
                                                addToContract: newVal ? true : false,
                                            };
                                            validate(next);
                                            return next;
                                        });
                                    }}
                                >
                                    Primary contact
                                    {errors.isPrimaryContact && (
                                        <div className="invalid-feedbacks">{errors.isPrimaryContact}</div>
                                    )}
                                </div>
                                {showSecondaryContact && (
                                    <div 
                                        className={`obd-contact-modal__type-tile obd-contact-modal__type-tile--secondary ${form.isSecondaryContact ? 'obd-contact-modal__type-tile--selected' : ''} obd-contact-modal__type-tile--disabled`}
                                    >
                                        Secondary contact
                                    </div>
                                )}
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.onUtilityBill ? 'obd-contact-modal__type-tile--selected' : ''}`}
                                    onClick={() => {
                                        if (isContactTypeLocked) return;
                                        update('onUtilityBill', !form.onUtilityBill);
                                    }}
                                >
                                    On utility bill
                                </div>
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.addToContract ? 'obd-contact-modal__type-tile--selected' : ''} ${form.isPrimaryContact || isContactTypeLocked ? 'obd-contact-modal__type-tile--disabled' : ''}`}
                                    onClick={() => {
                                        if (isContactTypeLocked) return;
                                        if (form.isPrimaryContact || isContactTypeLocked) return;

                                        const newVal = !form.addToContract;
                                        if (newVal) {
                                            const contractCount = getTotalAddToContractCount();
                                            if (contractCount >= 2) {
                                                setWarningDialog({
                                                    title: 'Contract Limit Reached',
                                                    content: 'You can only add up to two contacts to the contract. Please unselect anyone and try again.',
                                                });
                                                return;
                                            }

                                            const name = `${form.firstName} ${form.lastName}`.trim() || 'Customer';
                                            noticeAPI.info({
                                                message: `${name} is being added as a signee to the contract by clicking on the 'Add to Contract' button and will be required to sign either Third Party or Trinity Agreements. If the 'Add to Contract' button was clicked by mistake, please uncheck it.`,
                                                placement: 'topRight',
                                                duration: 10,
                                            });
                                        }

                                        setForm(prev => {
                                            const next = {
                                                ...prev,
                                                addToContract: newVal,
                                                // Match Angular addToContactCheck: non-primary + on contract => secondary; off contract => clear secondary.
                                                ...(newVal === true && !prev.isPrimaryContact ? { isSecondaryContact: true } : {}),
                                                ...(newVal === false && !prev.isPrimaryContact ? { isSecondaryContact: false } : {}),
                                            };
                                            validate(next);
                                            return next;
                                        });
                                    }}
                                >
                                    Add to contract
                                </div>
                                {showHomeOwner && (
                                    <div 
                                        className={`obd-contact-modal__type-tile ${form.homeowner ? 'obd-contact-modal__type-tile--selected' : ''} ${(isHomeOwnerReadOnly || isContactTypeLocked) ? 'obd-contact-modal__type-tile--disabled' : ''}`}
                                        onClick={() => {
                                            if (isContactTypeLocked || isHomeOwnerReadOnly) return;
                                            const newVal = !form.homeowner;
                                            if (newVal) {
                                                const hoCount = getTotalHomeownerCount();
                                                if (hoCount >= 2) {
                                                    setWarningDialog({
                                                        title: 'Homeowner Limit',
                                                        content: 'You can not select more than two homeowners. Please unselect anyone and try again.',
                                                    });
                                                    return;
                                                }
                                            }

                                            setForm(prev => {
                                                const next = { ...prev, homeowner: newVal };
                                                validate(next);
                                                return next;
                                            });
                                        }}
                                    >
                                        Homeowner
                                    </div>
                                )}
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.onTitle ? 'obd-contact-modal__type-tile--selected' : ''}`}
                                    onClick={() => {
                                        if (isContactTypeLocked) return;
                                        update('onTitle', !form.onTitle);
                                    }}
                                >
                                    On title
                                </div>
                            </div>
                        </section>

                        <section className="obd-contact-modal__section">
                            <div className="obd-contact-modal__mailing">
                                <h3 className="obd-contact-modal__section-title">Mailing address</h3>
                                <Checkbox
                                    checked={form.sameAsInstallationAddress}
                                    onChange={(e) => {
                                        if (isCustomerInfoAndAddressLocked) return;
                                        const checked = e.target.checked;
                                        setForm(prev => {
                                            const next = { ...prev, sameAsInstallationAddress: checked };
                                            if (checked) {
                                                // Backup CURRENT custom address before overwriting
                                                setMailingAddressBackup({
                                                    mailingStreet: prev.mailingStreet,
                                                    mailingCity: prev.mailingCity,
                                                    mailingState: prev.mailingState,
                                                    mailingZip: prev.mailingZip
                                                });

                                                if (opportunityDetails?.InstallationAddress) {
                                                    const addr = opportunityDetails.InstallationAddress;
                                                    next.mailingStreet = addr.Street || addr.street || '';
                                                    next.mailingCity = addr.City || addr.city || '';
                                                    next.mailingState = addr.State || addr.state || addr.stateCode || '';
                                                    next.mailingZip = addr.ZipCode || addr.zipCode || '';
                                                }
                                            } else if (mailingAddressBackup) {
                                                // Restore from backup if unchecking
                                                next.mailingStreet = mailingAddressBackup.mailingStreet || '';
                                                next.mailingCity = mailingAddressBackup.mailingCity || '';
                                                next.mailingState = mailingAddressBackup.mailingState || '';
                                                next.mailingZip = mailingAddressBackup.mailingZip || '';
                                            }
                                            validate(next);
                                            return next;
                                        });
                                    }}
                                    className="obd-contact-modal__checkbox"
                                    disabled={isCustomerInfoAndAddressLocked}
                                >
                                    Same as installation address
                                </Checkbox>
                            </div>

                            {!form.sameAsInstallationAddress && (
                                <div className="obd-contact-modal__fields">
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">Street *</label>
                                        <Input
                                            value={form.mailingStreet}
                                            onChange={(e) => update('mailingStreet', e.target.value)}
                                            placeholder="Street"
                                            className={`obd-contact-modal__input ${errors.mailingStreet ? 'is-invalid' : ''}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {errors.mailingStreet && <div className="invalid-feedbacks">{errors.mailingStreet}</div>}
                                    </div>
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">City *</label>
                                        <Input
                                            value={form.mailingCity}
                                            onChange={(e) => update('mailingCity', e.target.value)}
                                            placeholder="City"
                                            className={`obd-contact-modal__input ${errors.mailingCity ? 'is-invalid' : ''}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {errors.mailingCity && <div className="invalid-feedbacks">{errors.mailingCity}</div>}
                                    </div>
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">State *</label>
                                        <OBDSelect
                                            value={form.mailingState || undefined}
                                            onChange={(v) => update('mailingState', v)}
                                            options={states}
                                            placeholder="State"
                                            className={`obd-contact-modal__select ${errors.mailingState ? 'is-invalid-select' : ''}`}
                                            popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {errors.mailingState && <div className="invalid-feedbacks">{errors.mailingState}</div>}
                                    </div>
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">Zip Code *</label>
                                        <Input
                                            value={form.mailingZip}
                                            onChange={(e) => update('mailingZip', e.target.value)}
                                            placeholder="Zip code"
                                            className={`obd-contact-modal__input ${errors.mailingZip ? 'is-invalid' : ''}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {errors.mailingZip && <div className="invalid-feedbacks">{errors.mailingZip}</div>}
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>
                </div>
    );

    const formBody = (
        <>
            {noticeContext}
            <div className="obd-contact-modal__inner">{formFields}</div>
        </>
    );

    if (!open) return null;

    if (presentation === 'panel') {
        const footerActions = panelFooterPortal
            ? createPortal(
                  <div className="obd-contact-modal__actions obd-contact-modal__actions--footer">{actionButtons}</div>,
                  panelFooterPortal,
              )
            : null;

        return (
            <div className={`obd-contact-modal obd-contact-modal--panel obd-theme-${theme}`}>
                <div className="obd-contact-modal__panel-scroll obd-scroll-minimal">
                    {noticeContext}
                    <div className="obd-contact-modal__inner obd-contact-modal__inner--panel">{formFields}</div>
                </div>
                {footerActions}
                {confirmDialogs}
            </div>
        );
    }

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title={displayName}
            footer={footerActions}
            width={720}
            theme={theme}
            className={`obd-contact-modal obd-theme-${theme}`}
            destroyOnClose
            centered
            maskClosable={false}
        >
            {formBody}
            {confirmDialogs}
        </DashboardModal>
    );
};

export default ContactManageModal;
